import { useState } from "react";
import { todayYmd, receiptDateToYmd } from "@shared/dates";
import { ExpenseHistory, ExpenseStory, EditedTag } from "@/components/History";
import { EditExpenseSheet } from "@/components/EditExpenseSheet";
import { hasFullAccess } from "@shared/access";
import { useQuery, useMutation } from "@tanstack/react-query";
import { track } from "@/lib/analytics";
import { maybeAskForReview } from "@/lib/native-review";
import { apiRequest, apiFormRequest, queryClient } from "@/lib/queryClient";
import type { SafeUser, Expense } from "@shared/schema";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Receipt, CheckCircle2, HandCoins, Bell, AlertTriangle, UserMinus, Camera, X, Mail, Loader2, FileText, Upload, MoreVertical, Download, Repeat, Crown, Copy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { isInTWA } from "@/lib/platform";
import { ScanReceiptButton } from "@/components/ScanReceiptButton";
import { AddExpenseSheet } from "@/components/AddExpenseSheet";
import { CurrencySelector, formatMoney } from "@/components/CurrencySelector";
import { ExpenseAmount } from "@/components/ExpenseAmount";
import { FitText } from "@/components/FitText";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/lib/auth";
import { calculateGroupBalances } from "@/lib/simplify";
import { displayBalance, AMOUNT_IN_CLASS, AMOUNT_OUT_CLASS } from "@/lib/balance-display";
import { PaymentMethodsView } from "@/components/PaymentMethods";
import { ReminderSheet } from "@/components/ReminderSheet";

// Clamp friend avatars to the warm cream/terracotta palette (matches the
// Dashboard + Friends list). Deterministic per person.
const WARM_AVATARS = ["#7A3E32", "#8C5A3C", "#9A4A2A", "#A6674A", "#8A6A32", "#B04A34", "#6B4A3A", "#B5794A"];
function warmAvatar(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return WARM_AVATARS[h % WARM_AVATARS.length];
}

/**
 * Derive one participant's share of an expense from the STORED values only —
 * this is display-only and never affects balance math (see simplify.ts).
 * `splitAmounts` is a JSON map {userId: amount} for unequal splits; when it's
 * null the expense is divided equally among everyone in `splitAmongIds`.
 */
function participantShare(expense: Expense, userId: string): number {
  const ids = expense.splitAmongIds || [];
  let custom: Record<string, number> | null = null;
  if (expense.splitAmounts) {
    try { custom = JSON.parse(expense.splitAmounts); } catch { custom = null; }
  }
  if (custom) return Number(custom[userId] ?? 0);
  if (!ids.includes(userId)) return 0;
  return ids.length > 0 ? expense.amount / ids.length : 0;
}

// Human label for how an expense is split — derived from the ACTUAL shares, not
// just whether custom amounts were stored. "full amount" when only one person
// actually owes (e.g. you fronted it and they pay it all back), "split equally"
// when everyone who owes pays the same, otherwise "unequal split".
function splitLabel(expense: Expense): string {
  const ids = expense.splitAmongIds || [];
  const shares = ids.map((id) => participantShare(expense, id)).filter((s) => s > 0.005);
  if (shares.length <= 1) return "full amount";
  const allEqual = shares.every((s) => Math.abs(s - shares[0]) < 0.01);
  return allEqual ? "split equally" : "unequal split";
}

export default function FriendDetail({ friendId }: { friendId: string }) {
  const { user } = useAuth();
  const userCurrency = user?.defaultCurrency;
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [settleUpOpen, setSettleUpOpen] = useState(false);
  const [editExpense, setEditExpense] = useState<Expense | null>(null);

  // Splitwise import state
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState<"upload" | "map" | "preview">("upload");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importCsvNames, setImportCsvNames] = useState<string[]>([]);
  const [importImporterName, setImportImporterName] = useState("");
  const [importPreview, setImportPreview] = useState<{ date: string; description: string; cost: string }[]>([]);

  // Delete friend: 2-step confirmation
  const [deleteFriendStep, setDeleteFriendStep] = useState<0 | 1 | 2>(0); // 0=closed, 1=first confirm, 2=final warning

  // Delete expense confirmation
  const [deleteExpenseId, setDeleteExpenseId] = useState<string | null>(null);

  // Receipt detail dialog
  const [receiptExpenseId, setReceiptExpenseId] = useState<string | null>(null);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [detailExpense, setDetailExpense] = useState<Expense | null>(null);
  const [remindOpen, setRemindOpen] = useState(false);

  const handleViewReceipt = async (expenseId: string) => {
    setReceiptExpenseId(expenseId);
    setReceiptLoading(true);
    setReceiptData(null);
    try {
      const res = await apiRequest("GET", `/api/expenses/${expenseId}/receipt`);
      const data = await res.json();
      setReceiptData(data);
    } catch {
      setReceiptData(null);
    } finally {
      setReceiptLoading(false);
    }
  };

  const { data: friendsList = [] } = useQuery<SafeUser[]>({
    queryKey: ["/api/friends"],
  });

  const { data: directExpenses = [] } = useQuery<Expense[]>({
    queryKey: ["/api/friends/expenses"],
  });

  const friend = friendsList.find((f) => f.id === friendId);

  // Filter expenses to only those between the current user and this friend
  const friendExpenses = directExpenses.filter(
    (e) =>
      (e.paidById === user?.id && e.splitAmongIds.includes(friendId)) ||
      (e.paidById === friendId && e.splitAmongIds.includes(user?.id || ""))
  );

  const sortedExpenses = [...friendExpenses].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Calculate balance between just this user and this friend.
  // displayBalance() snaps sub-$0.05 rounding residuals to 0 so the friend
  // detail page shows "All settled up" when fully paid (instead of phantom
  // cents from non-evenly-divisible split math).
  const friendBalances = calculateGroupBalances(friendExpenses);
  const rawMyBalance = friendBalances.find((b) => b.personId === user?.id)?.amount || 0;
  const myBalance = displayBalance(rawMyBalance);


  const settleUpMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/settle-up", {
        friendId: friendId,
        amount: Math.abs(myBalance),
        // myBalance > 0 means the friend owes you → they're the one paying
        // it back. The server uses this to record the settlement in the
        // correct direction (otherwise it doubles the balance).
        friendIsPayer: myBalance > 0,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
      track("expense_settled", { context: "friend", amount: Math.abs(myBalance) });
      setSettleUpOpen(false);
      toast({ title: "Settled up", description: "Payment recorded" });
      maybeAskForReview("settle_up");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/expenses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/friends", friendId, "history"] });
      queryClient.invalidateQueries({ queryKey: ["/api/activity"] });
      setDeleteExpenseId(null);
      toast({ title: "Expense deleted" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
      setDeleteExpenseId(null);
    },
  });

  const exportFriendMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/export/expenses", {
        scope: "friend",
        friendId,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: "Export sent", description: data.message || "CSV sent to your email" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const removeFriendMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/friends/${friendId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
      queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
      setDeleteFriendStep(0);
      toast({ title: "Friend removed" });
      setLocation("/friends");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteAllExpensesMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/friends/${friendId}/expenses`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/friends", friendId] });
      toast({ title: "All expenses deleted" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const importFriendMutation = useMutation({
    mutationFn: async () => {
      if (!importFile || !importImporterName) return;
      const formData = new FormData();
      formData.append("file", importFile);
      formData.append("importerName", importImporterName);
      const res = await fetch(`/api/friends/${friendId}/import-splitwise`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Import failed");
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
      setImportOpen(false);
      setImportStep("upload");
      setImportFile(null);
      setImportImporterName("");
      setImportPreview([]);
      toast({
        title: "Import complete",
        description: `${data.imported} expenses imported${data.skipped > 0 ? `, ${data.skipped} skipped` : ""}.`,
      });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Import failed", description: msg, variant: "destructive" });
    },
  });

  // Can the current user delete this expense?
  const canDeleteExpense = (expense: Expense) => {
    return expense.addedById === user?.id || user?.isAdmin;
  };

  if (!friend) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 bg-muted animate-pulse rounded" />
        <div className="h-20 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header — one row: back, avatar + name, ⋮, and a round + (add
          expense). The icon-only + leaves the name room for two lines. */}
      <div className="flex items-center gap-2">
        <Link href="/friends">
          <Button size="icon" variant="ghost" data-testid="back-to-friends">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-semibold shrink-0"
            style={{ backgroundColor: warmAvatar(friend.id) }}
          >
            {friend.name[0]?.toUpperCase()}
          </div>
          <h1 className="flex-1 min-w-0 font-serif text-[26px] leading-[1.05] tracking-tight line-clamp-2 break-words">{friend.name}</h1>
        </div>
        {/* ⋮ Three-dot menu: Import + Remove Friend */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="shrink-0" data-testid="friend-detail-more-btn">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {/* The compact header has no room for the email — it lives here. */}
            <DropdownMenuLabel className="font-normal">
              <p className="text-sm font-semibold truncate">{friend.name}</p>
              <p className="text-xs text-muted-foreground font-mono truncate">{friend.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(friend.email);
                  toast({ title: "Email copied" });
                } catch {
                  toast({ title: "Couldn't copy", description: friend.email, variant: "destructive" });
                }
              }}
              data-testid="friend-copy-email"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy email
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setImportOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Import from another app
            </DropdownMenuItem>
            {friendExpenses.length > 0 && (
              <DropdownMenuItem onClick={() => exportFriendMutation.mutate()} disabled={exportFriendMutation.isPending}>
                <Download className="w-4 h-4 mr-2" />
                Export expenses
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {friendExpenses.length > 0 && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => {
                  if (confirm(`Delete all ${friendExpenses.length} expenses with ${friend.name}? This cannot be undone.`)) {
                    deleteAllExpensesMutation.mutate();
                  }
                }}
                disabled={deleteAllExpensesMutation.isPending}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete All Expenses
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDeleteFriendStep(1)}
            >
              <UserMinus className="w-4 h-4 mr-2" />
              Remove Friend
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Import Dialog (triggered from ⋮ menu) */}
        <Dialog open={importOpen} onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) { setImportStep("upload"); setImportFile(null); setImportImporterName(""); setImportPreview([]); }
        }}>
          <DialogTrigger className="hidden" />
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import from Splitwise</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {importStep === "upload" && (
                <>
                  <p className="text-sm text-muted-foreground">
                    Export your Splitwise history with <span className="font-medium text-foreground">{friend.name}</span> as a CSV, then upload it here.
                  </p>
                  <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside bg-muted/40 rounded-lg p-3">
                    <li>In Splitwise, open your friend or group with {friend.name}</li>
                    <li>Tap the settings / export icon and choose "Export to CSV"</li>
                    <li>Upload that CSV file below</li>
                  </ol>
                  <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 p-6 cursor-pointer hover:bg-muted/40 transition-colors">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                    {importFile ? (
                      <p className="text-sm font-medium text-foreground">{importFile.name}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">Tap to select CSV file</p>
                    )}
                    <input type="file" accept=".csv" className="hidden" onChange={(e) => {
                      const f = e.target.files?.[0]; if (f) setImportFile(f);
                    }} />
                  </label>
                  <Button className="w-full" disabled={!importFile || importLoading} onClick={async () => {
                    if (!importFile) return;
                    setImportLoading(true);
                    try {
                      const text = await importFile.text();
                      const allLines = text.split(/\r?\n/).filter(l => l.trim());
                      if (allLines.length < 2) { toast({ title: "Error", description: "CSV is empty", variant: "destructive" }); return; }
                      // Friends CSV starts with a "Note:" line — find the real header row by locating "Currency"
                      const headerIdx = allLines.findIndex(l =>
                        l.split(",").some(h => h.replace(/^"|"$/g, "").trim().toLowerCase() === "currency")
                      );
                      if (headerIdx === -1) { toast({ title: "Error", description: "Invalid Splitwise CSV — missing Currency column", variant: "destructive" }); return; }
                      const lines = allLines.slice(headerIdx); // lines[0] = header, lines[1+] = data
                      const headers = lines[0].split(",").map(h => h.replace(/^"|"$/g, "").trim());
                      const currIdx = headers.findIndex(h => h.toLowerCase() === "currency");
                      const csvNames = headers.slice(currIdx + 1).map(n => n.trim()).filter(n => n);
                      if (csvNames.length < 2) { toast({ title: "Error", description: "CSV must have at least 2 person columns", variant: "destructive" }); return; }
                      // Build preview
                      const dateIdx = headers.findIndex(h => h.toLowerCase() === "date");
                      const descIdx = headers.findIndex(h => h.toLowerCase() === "description");
                      const costIdx = headers.findIndex(h => h.toLowerCase() === "cost");
                      const preview = lines.slice(1).map(line => {
                        const cols = line.split(",").map(c => c.replace(/^"|"$/g, "").trim());
                        return { date: cols[dateIdx] || "", description: cols[descIdx] || "", cost: cols[costIdx] || "0" };
                      }).filter(r => r.description && !r.description.toLowerCase().includes("total balance"));
                      setImportCsvNames(csvNames);
                      setImportPreview(preview);
                      // Auto-detect importer column: match against current user's name
                      const userName = user?.name?.toLowerCase() || "";
                      const autoMatch = csvNames.find(n => n.toLowerCase() === userName || userName.includes(n.toLowerCase()) || n.toLowerCase().includes(userName));
                      if (autoMatch) setImportImporterName(autoMatch);
                      setImportStep("map");
                    } catch {
                      toast({ title: "Error", description: "Failed to parse CSV", variant: "destructive" });
                    } finally {
                      setImportLoading(false);
                    }
                  }}>
                    {importLoading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Parsing...</> : "Continue"}
                  </Button>
                </>
              )}

              {importStep === "map" && (
                <>
                  <p className="text-sm text-muted-foreground">
                    Which column in the CSV is <span className="font-medium text-foreground">you</span>? The other column(s) will be mapped to <span className="font-medium text-foreground">{friend.name}</span>.
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs font-medium">Your name in the CSV</p>
                    <Select value={importImporterName} onValueChange={setImportImporterName}>
                      <SelectTrigger><SelectValue placeholder="Select your column..." /></SelectTrigger>
                      <SelectContent>
                        {importCsvNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {importImporterName && (
                    <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
                      <p>✓ <span className="text-foreground font-medium">{importImporterName}</span> → You</p>
                      {importCsvNames.filter(n => n !== importImporterName).map(n => (
                        <p key={n}>✓ <span className="text-foreground font-medium">{n}</span> → {friend.name}</p>
                      ))}
                      <p className="pt-1 border-t border-border mt-1">{importPreview.length} expenses found in CSV</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setImportStep("upload")}>Back</Button>
                    <Button
                      className="flex-1"
                      disabled={!importImporterName || importFriendMutation.isPending}
                      onClick={() => importFriendMutation.mutate()}
                    >
                      {importFriendMutation.isPending ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Importing...</> : `Import ${importPreview.length} expenses`}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Button size="icon" className="rounded-full shrink-0" aria-label="Add expense" data-testid="add-friend-expense-btn" onClick={() => setAddExpenseOpen(true)}>
          <Plus className="w-4 h-4" />
        </Button>
        <AddExpenseSheet
          open={addExpenseOpen}
          onOpenChange={setAddExpenseOpen}
          people={[{ id: user?.id || "", name: user?.name || "You" }, { id: friendId, name: friend.name }]}
          friendId={friendId}
          invalidateKeys={[["/api/friends/expenses"]]}
        />
      </div>

      {/* Balance hero */}
      {myBalance === 0 ? (
        <p className="font-serif text-4xl text-muted-foreground tracking-tight">All settled up</p>
      ) : (
        <div>
          <p className="text-sm text-muted-foreground mb-1">
            {myBalance > 0
              ? <><span className="font-medium text-foreground">{friend.name}</span> owes you</>
              : <>You owe <span className="font-medium text-foreground">{friend.name}</span></>}
          </p>
          <FitText max={60} className={`font-serif leading-none tracking-tight ${myBalance > 0 ? AMOUNT_IN_CLASS : AMOUNT_OUT_CLASS}`}>
            {formatMoney(Math.abs(myBalance), userCurrency)}
          </FitText>
        </div>
      )}

      {/* Actions — Settle up always; Remind only when they owe you */}
      {myBalance !== 0 && (
        <div className="flex gap-2">
          <Button className="flex-1 rounded-full" onClick={() => setSettleUpOpen(true)} data-testid="friend-detail-settle-up">
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            Settle up
          </Button>
          {myBalance > 0 && (
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setRemindOpen(true)} data-testid="friend-detail-remind">
              <Bell className="w-4 h-4 mr-1.5" />
              Remind
            </Button>
          )}
        </div>
      )}

      {/* How this friend gets paid — shown when YOU owe them (you need where to
          send the money). Hidden when they owe you. */}
      {myBalance < 0 && (
        <Card className="p-4">
          <PaymentMethodsView userId={friend.id} name={friend.name} />
        </Card>
      )}

      {/* Reminder sheet — only relevant when they owe you */}
      {myBalance > 0 && (
        <ReminderSheet
          open={remindOpen}
          onClose={() => setRemindOpen(false)}
          recipientId={friend.id}
          recipientName={friend.name}
          amount={myBalance}
        />
      )}

      {/* Settle Up Dialog */}
      <Dialog open={settleUpOpen} onOpenChange={setSettleUpOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Settle Up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="rounded-lg bg-muted/50 p-4 text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                {myBalance < 0 ? (
                  <>You pay <span className="font-semibold text-foreground">{friend.name}</span></>
                ) : (
                  <><span className="font-semibold text-foreground">{friend.name}</span> pays you</>
                )}
              </p>
              <p className="text-2xl font-bold text-primary">
                {formatMoney(Math.abs(myBalance), userCurrency)}
              </p>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              This will record a payment that zeros out your balance with {friend.name}.
            </p>
            <Button
              className="w-full"
              onClick={() => settleUpMutation.mutate()}
              disabled={settleUpMutation.isPending}
              data-testid="confirm-friend-detail-settle-up"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              {settleUpMutation.isPending ? "Settling..." : "Confirm Settlement"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt detail dialog */}
      <Dialog open={!!receiptExpenseId} onOpenChange={(open) => { if (!open) { setReceiptExpenseId(null); setReceiptData(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {receiptData?.merchant || "Receipt Details"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            {receiptLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground ml-2">Loading receipt...</span>
              </div>
            ) : receiptData ? (
              <>
                {receiptData.items?.length > 0 && (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {receiptData.items.map((item: any, i: number) => (
                          <tr key={i} className={i % 2 === 0 ? "bg-muted/30" : ""}>
                            <td className="px-3 py-2 text-foreground">{item.name}</td>
                            <td className="px-3 py-2 text-right font-medium text-foreground whitespace-nowrap">
                              ${typeof item.price === "number" ? item.price.toFixed(2) : item.price}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="border-t border-border px-3 py-2 space-y-1">
                      {receiptData.subtotal != null && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Subtotal</span>
                          <span>${receiptData.subtotal.toFixed(2)}</span>
                        </div>
                      )}
                      {receiptData.tax != null && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Tax</span>
                          <span>${receiptData.tax.toFixed(2)}</span>
                        </div>
                      )}
                      {receiptData.total != null && (
                        <div className="flex justify-between text-sm font-semibold text-foreground pt-1 border-t border-border">
                          <span>Total</span>
                          <span>${receiptData.total.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {(!receiptData.items || receiptData.items.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No items extracted from this receipt.</p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No receipt data available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Expense Details Dialog — full name + split breakdown (display only) */}
      <Dialog open={!!detailExpense} onOpenChange={(open) => { if (!open) setDetailExpense(null); }}>
        {/* Don't auto-focus the first button (Edit / Restore): phones draw a
            focus ring on it as soon as the detail opens. */}
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="pr-6 break-words">
              {detailExpense?.isSettlement ? "Settlement" : detailExpense?.description}
            </DialogTitle>
          </DialogHeader>
          {detailExpense && (() => {
            const exp = detailExpense;
            const yourShare = participantShare(exp, user?.id || "");
            const friendShare = participantShare(exp, friendId);
            const ids = exp.splitAmongIds || [];
            const othersCount = ids.filter((id) => id !== user?.id && id !== friendId).length;
            const othersTotal = Math.max(0, exp.amount - yourShare - friendShare);
            return (
              <div className="space-y-4 pt-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-muted-foreground min-w-0 truncate">
                    {exp.paidById === user?.id ? "You" : (friend?.name || "Your friend")} paid
                  </span>
                  <ExpenseAmount
                    amount={exp.amount}
                    currency={exp.currency}
                    baseCurrency={exp.baseCurrency}
                    originalAmount={exp.originalAmount}
                    viewerCurrency={userCurrency}
                    className="font-mono font-semibold text-lg"
                  />
                </div>
                <ExpenseStory
                  expense={exp}
                  source={{ friendId }}
                  meId={user?.id ?? ""}
                  isAdmin={!!user?.isAdmin}
                  nameOf={(id) => (id === user?.id ? "You" : id === friendId ? (friend?.name || "Your friend") : "Someone")}
                  onRestored={() => {
                    setDetailExpense(null);
                    queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
                    queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
                  }}
                />
                {!exp.isSettlement && (
                  <div className="rounded-lg border p-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      {splitLabel(exp)}
                      {ids.length > 0 ? ` · ${ids.length} ${ids.length === 1 ? "person" : "people"}` : ""}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <span>You</span>
                      <span className="font-mono">{formatMoney(yourShare, userCurrency)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate">{friend?.name || "Your friend"}</span>
                      <span className="font-mono shrink-0">{formatMoney(friendShare, userCurrency)}</span>
                    </div>
                    {othersCount > 0 && (
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{othersCount} other{othersCount > 1 ? "s" : ""}</span>
                        <span className="font-mono">{formatMoney(othersTotal, userCurrency)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex items-center justify-between gap-3 text-sm font-medium">
                      <span className="min-w-0 truncate">{exp.paidById === user?.id ? `${friend?.name || "Your friend"} owes you` : "You owe"}</span>
                      <span className={`font-mono shrink-0 ${exp.paidById === user?.id ? AMOUNT_IN_CLASS : AMOUNT_OUT_CLASS}`}>
                        {formatMoney(exp.paidById === user?.id ? friendShare : yourShare, userCurrency)}
                      </span>
                    </div>
                  </div>
                )}
                {!exp.deletedAt && (exp as any).receiptData && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setDetailExpense(null); handleViewReceipt(exp.id); }}
                    data-testid="view-receipt-from-detail"
                  >
                    <FileText className="w-4 h-4 mr-2" /> View receipt items
                  </Button>
                )}
                {!exp.deletedAt && canDeleteExpense(exp) && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setEditExpense(exp); setDetailExpense(null); }}
                    data-testid="edit-expense-from-detail"
                  >
                    Edit expense
                  </Button>
                )}
                {!exp.deletedAt && canDeleteExpense(exp) && (
                  <Button
                    variant="outline"
                    className="w-full text-destructive hover:text-destructive"
                    onClick={() => { setDetailExpense(null); setDeleteExpenseId(exp.id); }}
                    data-testid="delete-expense-from-detail"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete expense
                  </Button>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Expense Confirmation Dialog */}
      <Dialog open={!!deleteExpenseId} onOpenChange={(open) => { if (!open) setDeleteExpenseId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3 rounded-lg bg-destructive/10 p-4">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
              <p className="text-sm text-foreground">
                Are you sure you want to delete this expense? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteExpenseId(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => {
                  if (deleteExpenseId) deleteExpenseMutation.mutate(deleteExpenseId);
                }}
                disabled={deleteExpenseMutation.isPending}
                data-testid="confirm-delete-expense"
              >
                {deleteExpenseMutation.isPending ? "Deleting..." : "Yes, Delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Friend 2-step Confirmation Dialog */}
      <Dialog open={deleteFriendStep > 0} onOpenChange={(open) => { if (!open) setDeleteFriendStep(0); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {deleteFriendStep === 1 ? "Remove Friend" : "Final Warning"}
            </DialogTitle>
          </DialogHeader>
          {deleteFriendStep === 1 && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3 rounded-lg bg-destructive/10 p-4">
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
                <p className="text-sm text-foreground">
                  Are you sure you want to remove <strong>{friend.name}</strong> from your friends?
                </p>
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeleteFriendStep(0)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => setDeleteFriendStep(2)}
                >
                  Yes, Remove
                </Button>
              </div>
            </div>
          )}
          {deleteFriendStep === 2 && (
            <div className="space-y-4 pt-2">
              <div className="rounded-lg bg-destructive/10 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
                  <p className="text-sm font-semibold text-destructive">This is permanent</p>
                </div>
                <p className="text-sm text-foreground">
                  If you remove <strong>{friend.name}</strong>, all expense history between you two will also be deleted. This cannot be undone.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeleteFriendStep(0)}
                >
                  No, keep friend
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => removeFriendMutation.mutate()}
                  disabled={removeFriendMutation.isPending}
                  data-testid="confirm-delete-friend-final"
                >
                  {removeFriendMutation.isPending ? "Removing..." : "I understand, delete"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History — expenses by month plus what changed: deleted ones stay in
          place (struck, with Restore) and edits appear as their own entry.
          Tap a live row to open its detail (dates, edit, receipt, delete). */}
      <ExpenseHistory
        source={{ friendId }}
        liveExpenses={sortedExpenses}
        meId={user?.id ?? ""}
        nameOf={(id) => (id === user?.id ? "You" : id === friendId ? friend.name : "Someone")}
        onOpenDeleted={(e) => setDetailExpense(e)}
        emptyState={
          <Card className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-base font-semibold mb-1">No expenses yet</h3>
            <p className="text-sm text-muted-foreground">
              Add an expense to start tracking splits with {friend.name}.
            </p>
          </Card>
        }
        renderLive={(expense) => {
          const d = new Date(expense.date);
          const paidByName = expense.paidById === user?.id ? "You" : friend.name;
          const iPaid = expense.paidById === user?.id;
          const share = iPaid
            ? participantShare(expense, friendId)
            : participantShare(expense, user?.id || "");
          return (
            <div
              className="flex items-center gap-4 py-4 border-b border-border cursor-pointer"
              onClick={() => setDetailExpense(expense)}
              data-testid={`friend-expense-${expense.id}`}
            >
              <div className="w-11 text-center shrink-0">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                  {d.toLocaleDateString(undefined, { month: "short" })}
                </div>
                <div className="font-serif text-2xl leading-none">{d.getDate()}</div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold tracking-tight truncate">
                  {expense.isSettlement ? "Settlement" : expense.description}
                  {expense.updatedAt && <EditedTag />}
                </p>
                <p className="font-mono text-xs text-muted-foreground truncate mt-0.5">
                  {expense.isSettlement
                    ? "Payment"
                    : `${paidByName} paid · ${splitLabel(expense)}`}
                                  </p>
              </div>
              <ExpenseAmount
                amount={expense.isSettlement ? expense.amount : share}
                currency={expense.isSettlement ? expense.currency : null}
                baseCurrency={expense.isSettlement ? expense.baseCurrency : null}
                originalAmount={expense.isSettlement ? expense.originalAmount : null}
                viewerCurrency={userCurrency}
                className={`font-mono font-semibold ${expense.isSettlement ? "text-muted-foreground" : (iPaid ? AMOUNT_IN_CLASS : AMOUNT_OUT_CLASS)}`}
              />
            </div>
          );
        }}
      />

      <EditExpenseSheet
        expense={editExpense}
        open={!!editExpense}
        onClose={() => setEditExpense(null)}
        payerOptions={[{ id: user?.id ?? "", name: "You" }, { id: friendId, name: friend.name }]}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
          queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
          queryClient.invalidateQueries({ queryKey: ["/api/friends", friendId, "history"] });
        }}
      />

    </div>
  );
}
